import { useRef } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { ssoLoadingState } from '../stores/sso-loading.store'
import { authenticationState } from '../stores/authentication.store'
import { authenticationApi } from '../services/authentication.apis'
import { TAuthentication } from '../types'
import { TCustomer } from '../../user/types/customer.type'
import { customerState, defaultCustomer } from '../../user/stores/customer.store'
import { countryState } from '../../country/stores/country.store'

/** Storage keys for persistent authentication */
const STORAGE_KEYS = {
    AUTHENTICATION: 'u25_authentication',
    CUSTOMER: 'u25_customer',
    AUTH_TIMESTAMP: 'u25_auth_timestamp'
} as const

/** Session duration in milliseconds (24 hours) */
const SESSION_DURATION_MS = 24 * 60 * 60 * 1000

/**
 * Check if stored session is still valid
 */
const isSessionValid = (): boolean => {
    try {
        const timestamp = localStorage.getItem(STORAGE_KEYS.AUTH_TIMESTAMP)
        if (!timestamp) return false

        const storedTime = parseInt(timestamp, 10)
        const now = Date.now()
        return now - storedTime < SESSION_DURATION_MS
    } catch {
        return false
    }
}

/**
 * Get stored authentication data from localStorage
 */
const getStoredAuthentication = (): TAuthentication | null => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.AUTHENTICATION)
        if (!stored) return null
        return JSON.parse(stored) as TAuthentication
    } catch {
        return null
    }
}

/**
 * Get stored customer data from localStorage
 */
const getStoredCustomer = (): TCustomer | null => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.CUSTOMER)
        if (!stored) return null
        return JSON.parse(stored) as TCustomer
    } catch {
        return null
    }
}

/**
 * True when a non-expired session is persisted (token + within the 24h TTL). Lets the
 * app gate anonymous boot fetches until a restored session's authed status resolves,
 * so status-scoped endpoints load once instead of anonymous-then-authed.
 */
export const hasValidStoredSession = (): boolean => isSessionValid() && !!getStoredAuthentication()?.token

/**
 * Save authentication data to localStorage
 */
export const persistAuthData = (auth: TAuthentication, customer: TCustomer): void => {
    try {
        localStorage.setItem(STORAGE_KEYS.AUTHENTICATION, JSON.stringify(auth))
        localStorage.setItem(STORAGE_KEYS.CUSTOMER, JSON.stringify(customer))
        localStorage.setItem(STORAGE_KEYS.AUTH_TIMESTAMP, Date.now().toString())
    } catch (error) {
        console.error('Failed to persist authentication data:', error)
    }
}

/**
 * Clear all authentication data from localStorage
 */
const clearAuthData = (): void => {
    try {
        localStorage.removeItem(STORAGE_KEYS.AUTHENTICATION)
        localStorage.removeItem(STORAGE_KEYS.CUSTOMER)
        localStorage.removeItem(STORAGE_KEYS.AUTH_TIMESTAMP)
    } catch (error) {
        console.error('Failed to clear authentication data:', error)
    }
}

export const useAuthentication = ({
    enablePersist = true,
    onRefreshError
}: {
    enablePersist?: boolean
    /** Called after a background token refresh throws (any error): the stale session is
     *  cleared first, then this runs so the app can redirect (e.g. to home). Opt-in —
     *  without it, a failed refresh keeps the restored session (lenient default). */
    onRefreshError?: () => void
}) => {
    const restoredSessionRefresh = useRef<Promise<void> | null>(null)
    const loading = useAtomValue(ssoLoadingState)
    const authentication = useAtomValue(authenticationState)
    const country = useAtomValue(countryState)

    const setAuthentication = useSetAtom(authenticationState)
    const setCustomer = useSetAtom(customerState)

    const extractHydraToken = (payload: unknown): string | null => {
        const data = payload as { token?: string; payload?: { token?: string }; loginData?: { token?: string } } | undefined
        return data?.token ?? data?.payload?.token ?? data?.loginData?.token ?? null
    }

    /** Reset auth + customer atoms and wipe persisted session (localStorage + cart). */
    const clearSession = () => {
        setAuthentication({
            baId: '',
            customer: { href: '' },
            expired: false,
            href: '',
            log_id: null,
            duration: '',
            token: '',
            loginTokenSecondsLeft: 0
        })
        setCustomer(defaultCustomer)
        clearAuthData()
        sessionStorage.removeItem('cart')
    }

    /**
     * Refresh a restored token in background.
     * Keeps current auth as fallback when refresh fails.
     */
    const refreshRestoredSession = async (auth: TAuthentication, customer: TCustomer, countryCode3alpha: string) => {
        if (!countryCode3alpha || !auth.token || !auth.baId) return

        try {
            const response = await authenticationApi.refreshToken({
                countryCode3alpha,
                baId: auth.baId,
                loginId: auth.baId,
                token: auth.token
            })

            const refreshedToken = extractHydraToken(response?.data)
            if (!refreshedToken) return

            const refreshedAuth: TAuthentication = {
                ...auth,
                token: refreshedToken
            }

            setAuthentication(refreshedAuth)

            if (enablePersist) {
                persistAuthData(refreshedAuth, customer)
            }
        } catch (error) {
            // A refresh failure means the stored token can't be trusted. When the app
            // opts in, clear the stale session and let it redirect (e.g. to home);
            // otherwise keep the restored session as a lenient fallback.
            if (onRefreshError) {
                console.error('Refresh token failed, clearing session:', error)
                clearSession()
                onRefreshError()
            } else {
                console.error('Refresh token failed, keeping restored session:', error)
            }
        }
    }

    /**
     * Restore session from localStorage if valid
     * @returns true if session was restored successfully
     */
    const restoreSession = (countryCode3alpha?: string): boolean => {
        // Skip if already authenticated
        if (authentication.token && authentication.baId) {
            return true
        }

        if (!enablePersist) {
            return false
        }

        // Check if stored session is valid
        if (!isSessionValid()) {
            clearAuthData()
            return false
        }

        const storedAuth = getStoredAuthentication()
        const storedCustomer = getStoredCustomer()

        if (storedAuth?.token && storedAuth?.baId && storedCustomer) {
            setAuthentication(storedAuth)
            setCustomer(storedCustomer)
            const resolvedCountryCode = countryCode3alpha || country.short
            restoredSessionRefresh.current = refreshRestoredSession(storedAuth, storedCustomer, resolvedCountryCode)
            return true
        }

        return false
    }

    /**
     * Entry point for authentication on app load.
     * SSO is now handled by useSsoEntry in the consuming app;
     * this function restores from localStorage and waits for its token refresh
     * so startup callers can safely load authenticated data once.
     */
    const authenSso = async (countryCode3alpha?: string) => {
        if (restoreSession(countryCode3alpha || country.short)) await restoredSessionRefresh.current
    }

    /**
     * Logout and clear all stored authentication data
     */
    const logout = () => {
        clearSession()
    }

    /**
     * Check if user is currently authenticated
     */
    const isAuthenticated = Boolean(authentication.token && authentication.baId)

    return {
        authenSso,
        restoreSession,
        logout,
        loading,
        isAuthenticated
    }
}
